import type { FormEvent } from 'react'
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { KeyRound, LogOut, Undo2 } from 'lucide-react'
import { api, errorMessage, fieldErrors } from '@/lib/api'
import { useAuth } from '@/context/AuthContext'
import AvatarUploader from '@/components/AvatarUploader'
import Card, { SectionLabel } from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import { Field, PasswordInput } from '@/components/ui/Field'
import { useToast } from '@/components/ui/toast'

/**
 * Self-service account page, mounted for staff and admin alike. Identity is
 * read-only - name and email changes go through admin > Staff, because email
 * doubles as the invite and reset address.
 */
export default function Profile() {
  const { user, impersonating, logout, endImpersonation } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const [current, setCurrent] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')

  const change = useMutation({
    mutationFn: async () =>
      (
        await api.put('/profile/password', {
          current_password: current,
          password,
          password_confirmation: confirm,
        })
      ).data,
    onSuccess: (data) => {
      toast.success(data.message ?? 'Password updated')
      setCurrent('')
      setPassword('')
      setConfirm('')
    },
    onError: (err) => toast.error('Update failed', errorMessage(err)),
  })

  const errors = fieldErrors(change.error)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    change.mutate()
  }

  const onSignOut = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <div>
      <PageHeader title="My account" subtitle="Your details and sign-in settings" />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <SectionLabel>Account details</SectionLabel>
          {!impersonating && (
            <div className="mb-5 border-b border-foreground/10 pb-5">
              <AvatarUploader />
            </div>
          )}
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Name</dt>
              <dd className="min-w-0 break-all text-right font-semibold">{user?.name}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Email</dt>
              <dd className="min-w-0 break-all text-right font-semibold">{user?.email}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Role</dt>
              <dd className="font-semibold capitalize">{user?.role}</dd>
            </div>
            {user?.department && (
              <div className="flex justify-between gap-4">
                <dt className="text-muted">Section</dt>
                <dd className="font-semibold">{user.department.name}</dd>
              </div>
            )}
          </dl>
          <p className="mt-4 text-xs text-muted">
            Need a name or email change? Ask an admin - it affects your sign-in address.
          </p>
        </Card>

        <Card>
          <SectionLabel>Change password</SectionLabel>
          {/* An admin signed in as this member is here to look; the server
              refuses these anyway, and the admin sets passwords from Staff. */}
          {impersonating ? (
            <p className="text-sm text-muted">
              Not available while signed in as {user?.name}. Set their password from the admin console under
              Staff & Admins.
            </p>
          ) : (
          <form onSubmit={submit} className="space-y-4">
            <Field label="Current password" error={errors.current_password?.[0]}>
              <PasswordInput
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                required
              />
            </Field>
            <Field label="New password" error={errors.password?.[0]}>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </Field>
            <Field label="Confirm new password">
              <PasswordInput
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
              />
            </Field>
            <Button type="submit" loading={change.isPending}>
              <KeyRound className="size-4" /> Update password
            </Button>
            <p className="text-xs text-muted">Other signed-in devices are signed out when it changes.</p>
          </form>
          )}
        </Card>
      </div>

      {/* Also in the avatar menu on desktop, but the staff phone layout has no
          top bar, so this is the way out there. */}
      <div className="mt-5">
        {impersonating ? (
          <Button variant="outline" onClick={() => endImpersonation()} className="w-full sm:w-auto">
            <Undo2 className="size-4" /> Return to admin console
          </Button>
        ) : (
          <Button variant="outline" onClick={onSignOut} className="w-full sm:w-auto">
            <LogOut className="size-4" /> Sign out
          </Button>
        )}
      </div>
    </div>
  )
}
